import { useAtomValue } from "jotai";
import { SelectOption } from "@ir/ui";
import { AppConfig } from "@/infrastructure/services/config";
import { DateService } from "@/core/services/Date.service";
import { DateAdapter } from "@/infrastructure/adapters/dayJs";
import { useState } from "react";
import { ExportDeductionUseCase } from "@/core/usecases/deduction/ExportDeduction.useCase";
import { ExportDeductionRepository } from "@/infrastructure/repositories/export/export.deduction.repository";
import { IExportDeductionParams, IExportDeductionRequest } from "@/core/repositories/export/export.deduction";
import { searchRemittanceAtom } from "@/store/deduction/main";
import { useWorkspace } from "@/hooks/workspace";
import { prioritiesAtom, purchasersAtom, validitiesAtom, distributionCentersAtom } from "@/store/lookup/lookup.atoms";
import { useDeductionFilter } from "./useDeductionFilter";
import { useLookup } from "@/hooks";
import { Stage } from "@/core/entities/lookup";
import { getDefaultAnalystAtom } from "@/store/deduction/remittanceItem";

const TIMEZONE = AppConfig.getInstance().TIMEZONE;
const DEFAULT_DATE_FORMAT = AppConfig.getInstance().DEFAULT_DATE_FORMAT;

const dateService = new DateService(new DateAdapter());

export function useDeductionExport() {
  const purchasers = useAtomValue(purchasersAtom);
  const distributionCenters = useAtomValue(distributionCentersAtom);
  const priorities = useAtomValue(prioritiesAtom);
  const validities = useAtomValue(validitiesAtom);
  const searchRemittance = useAtomValue(searchRemittanceAtom);
  const { settings: workspaceSettings } = useWorkspace();
  const { quickFilterInfo, filterInfo } = useDeductionFilter();
  const { stagesLookup } = useLookup();
  const defaultAnalyst = useAtomValue(getDefaultAnalystAtom);

  const [isDeductionExporting, setIsDeductionExporting] = useState(false);

  const handleDeductionExport = async () => {
    setIsDeductionExporting(true);
    const isAllTab = quickFilterInfo.statusId === "all";

    const getStageIds = () => {
      switch (quickFilterInfo.statusId) {
        case "all":
          return filterInfo.progress.map((progress: SelectOption) => Number(progress.value));
        case "incomplete":
          return undefined;
        default:
          return [Number(quickFilterInfo.statusId)];
      }
    };

    const getAnalystIds = (): number[] | undefined => {
      if (isAllTab) {
        return filterInfo.analyst.length > 0
          ? filterInfo.analyst.map((analyst: SelectOption) => Number(analyst.value))
          : undefined;
      }
      return defaultAnalyst?.value ? [Number(defaultAnalyst.value)] : undefined;
    };

    const baseParams: IExportDeductionParams = {
      time_zone: TIMEZONE,
      check_from_date: null,
      check_to_date: null,
      status_ids: getStageIds(),
      analyst_ids: getAnalystIds(),
    };

    const params: IExportDeductionParams = isAllTab ? {
      ...baseParams,
      search: searchRemittance,
      check_to_date: filterInfo.checkToDate ?? null,
      check_from_date: filterInfo.checkFromDate ?? null,
      invoice_from_date: filterInfo.invoiceFromDate ?? undefined,
      invoice_to_date: filterInfo.invoiceToDate ?? undefined,
      purchaser_ids: filterInfo.purchaser.map((purchaser: SelectOption) => Number(purchaser.value)),
      distribution_center_ids: filterInfo.dc.map((dc: SelectOption) => Number(dc.value)),
      priority_ids: filterInfo.priority.map((priority: SelectOption) => Number(priority.value)),
      validity_ids: filterInfo.validity.map((validity: SelectOption) => Number(validity.value)),
      adjustment_type_ids: filterInfo.adjustmentTypes.map((adjustment: SelectOption) => Number(adjustment.value)),
      adjustment_category_ids: filterInfo.adjustmentCategories?.map((adjustment: SelectOption) => Number(adjustment.value)),
      deduction_type_category_ids: filterInfo.deductionTypeCategories?.map((category: SelectOption) => Number(category.value)),
      retailer_ids: filterInfo.retailer.map((retailer: SelectOption) => Number(retailer.value)),
      po_number_search: filterInfo.poNumberSearch ?? undefined,
      approver_ids: filterInfo.approver.map((approver: SelectOption) => Number(approver.value)),
    } : baseParams;

    const progressAppliedFilter = {
      name: "Progress",
      value: isAllTab
        ? filterInfo.progress.map((progress: SelectOption) => progress.label).join(", ")
        : stagesLookup.data?.find((stage: Stage) => stage.id === Number(quickFilterInfo.statusId))?.stageName ?? "",
    };

    const exportRequest: IExportDeductionRequest = {
      params: params,
      formats: {
        date: workspaceSettings?.displayDateFormat,
        number: workspaceSettings?.displayNumberFormat,
      },
      appliedFilters: isAllTab ? [
        {
          name: "Check Date",
          value: params.check_from_date && params.check_to_date
            ? `${dateService.format(params.check_from_date, workspaceSettings?.displayDateFormat ?? DEFAULT_DATE_FORMAT)} - ${dateService.format(params.check_to_date, workspaceSettings?.displayDateFormat ?? DEFAULT_DATE_FORMAT)}`
            : "",
        },
        {
          name: "Invoice Date",
          value:
            filterInfo.invoiceFromDate && filterInfo.invoiceToDate
              ? `${dateService.format(filterInfo.invoiceFromDate, workspaceSettings?.displayDateFormat ?? DEFAULT_DATE_FORMAT)} - ${dateService.format(filterInfo.invoiceToDate, workspaceSettings?.displayDateFormat ?? DEFAULT_DATE_FORMAT)}`
              : "",
        },
        {
          name: "Purchaser",
          value: filterInfo.purchaser.map((purchaser: SelectOption) => purchaser.label).join(", "),
        },
        {
          name: "DC",
          value: filterInfo.dc.map((dc: SelectOption) => dc.label).join(", "),
        },
        {
          name: "Priority",
          value: filterInfo.priority.map((priority: SelectOption) => priority.label).join(", "),
        },
        {
          name: "Validity",
          value: filterInfo.validity.map((validity: SelectOption) => validity.label).join(", "),
        },
        {
          name: "Adjustment Type",
          value: filterInfo.adjustmentTypes.map((adjustment: SelectOption) => adjustment.label).join(", "),
        },
        {
          name: "Adjustment Category",
          value: filterInfo.adjustmentCategories.map((adjustment: SelectOption) => adjustment.label).join(", "),
        },
        {
          name: "Deduction Type Category",
          value: filterInfo.deductionTypeCategories.map((category: SelectOption) => category.label).join(", "),
        },
        {
          name: "Retailer",
          value: filterInfo.retailer.map((retailer: SelectOption) => retailer.label).join(", "),
        },
        {
          name: "PO Number",
          value: filterInfo.poNumberSearch ?? "",
        },
        {
          name: "Analyst",
          value: filterInfo.analyst.map((analyst: SelectOption) => analyst.label).join(", "),
        },
        {
          name: "Approver",
          value: filterInfo.approver.map((approver: SelectOption) => approver.label).join(", "),
        },
        progressAppliedFilter,
      ] : [
        progressAppliedFilter,
        {
          name: "Analyst",
          value: defaultAnalyst?.label ?? "",
        },
      ],
    };
    return await new ExportDeductionUseCase(new ExportDeductionRepository()).execute(exportRequest);
  };

  return {
    filterInfo,
    purchasers,
    distributionCenters,
    priorities,
    validities,
    isDeductionExporting,
    handleDeductionExport,
    setIsDeductionExporting,
  };
}
